/**
 * A USB folder the queue is written to, chosen once in Settings ("USB Folder").
 *
 * A browser cannot see a flash drive being plugged in, so the drive is a folder the
 * scout picks once (File System Access API, Chrome/Edge). Its handle is kept in
 * IndexedDB - localStorage cannot hold one - and every Submit (and the Scanner's Upload /
 * Export) rewrites this laptop's queue file there: `scout-queue-<event>-<device>.json`,
 * the name Relay's Import Folder already reads. One file per laptop per event, rewritten
 * whole, so a re-import merges by id and never duplicates. A missing drive just fails the
 * write: "not on USB". The upload page's Export button picks a folder on the spot and
 * makes it the USB folder too.
 */
import { getQueue } from './queue';

const DB_NAME = 'osprey-usb';
const STORE = 'handles';
const HANDLE_KEY = 'folder';
const DEVICE_KEY = 'usb_device_tag';

export function usbSupported() {
    return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function'
        && typeof indexedDB !== 'undefined';
}

function openDb() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function withStore(mode, fn) {
    const db = await openDb();
    try {
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, mode);
            const req = fn(tx.objectStore(STORE));
            tx.oncomplete = () => resolve(req && req.result);
            tx.onerror = () => reject(tx.error);
        });
    } finally {
        db.close();
    }
}

export async function getUsbFolder() {
    if (!usbSupported()) return null;
    try {
        return (await withStore('readonly', store => store.get(HANDLE_KEY))) || null;
    } catch {
        return null;
    }
}

/** Ask the scout for the folder. Must run from a tap. @returns its name, or null if cancelled */
export async function chooseUsbFolder() {
    if (!usbSupported()) return null;
    try {
        // Opens in the last folder (usually the flash drive), so re-picking it is one click.
        const previous = await getUsbFolder();
        const handle = await window.showDirectoryPicker({ id: 'osprey-usb', mode: 'readwrite', ...(previous ? { startIn: previous } : {}) });
        await withStore('readwrite', store => store.put(handle, HANDLE_KEY));
        return handle.name;
    } catch (err) {
        if (err && err.name === 'AbortError') return null;
        throw err;
    }
}

export async function forgetUsbFolder() {
    if (!usbSupported()) return;
    try { await withStore('readwrite', store => store.delete(HANDLE_KEY)); } catch {}
}

/**
 * Make sure the folder may be written, asking if the browser needs to. Chrome can drop
 * the grant after a reload, and only lets a page ask during a tap, so Submit starts this
 * first thing and awaits it later. Never throws. @returns {Promise<boolean>}
 */
export async function primeUsbPermission() {
    const handle = await getUsbFolder();
    if (!handle) return false;
    try {
        const opts = { mode: 'readwrite' };
        if (await handle.queryPermission(opts) === 'granted') return true;
        return await handle.requestPermission(opts) === 'granted';
    } catch {
        return false;
    }
}

/** A short random tag, fixed per laptop, so two laptops' files never overwrite each other. */
function deviceTag() {
    try {
        let tag = localStorage.getItem(DEVICE_KEY);
        if (!tag) {
            tag = crypto.randomUUID().slice(0, 8);
            localStorage.setItem(DEVICE_KEY, tag);
        }
        return tag;
    } catch {
        return 'laptop';
    }
}

export function usbFileName(eventPath) {
    return `scout-queue-${eventPath || 'GLOBAL'}-${deviceTag()}.json`;
}

/**
 * Rewrite this laptop's queue file in the USB folder. Never throws.
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function writeQueueToUsb(eventPath) {
    const handle = await getUsbFolder();
    if (!handle) return { ok: false, reason: 'no USB folder chosen' };
    try {
        if (await handle.queryPermission({ mode: 'readwrite' }) !== 'granted') {
            return { ok: false, reason: 'no permission for the USB folder' };
        }
        const file = await handle.getFileHandle(usbFileName(eventPath), { create: true });
        const writable = await file.createWritable();
        await writable.write(JSON.stringify(getQueue(eventPath)));
        await writable.close();
        return { ok: true };
    } catch (err) {
        // NotFoundError when the drive is unplugged.
        return { ok: false, reason: err && err.message ? err.message : 'USB folder unavailable' };
    }
}
